import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ResetPasswordData, VerifyData, EmailData } from '../interfaces';

@Injectable()
export class AccountsService {
  constructor(private http: HttpClient) {}

  /**
   * Resets the password with the validation code that was sent via
   * email. A confirmation email is sent on success.
   */
  resetPassword(sport: string, data: ResetPasswordData): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/resetpassword`, data);
  }

  /**
   * Verify the user's account
   * When a new user gets invited to join an organization, a user
   * account is created for that individual. When this account is
   * created, they will be emailed a verification code. To confirm/
   * verify their account(i.e.: that email address is valid and
   * belongs to this user) and to set up the rest of the user account,
   * the user will need to click the link their verification email to
   * validate the account and set the password, birth date, etc.
   */
  verify(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/verifymember`, {
      emailAddress: data.emailAddress,
      password: data.password,
      birthMonth: data.birthdayMonth + 1,
      birthYear: data.birthdayYear,
      verificationCode: data.verificationCode,
      armyInterest: data.armyInterest,
      phoneNumber: this.formatPhoneNumber(data.phoneNumber)
    });
  }

  /**
   * Verify the user's account
   */
  verifyUser(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/verifyuser`, {
      emailAddress: data.emailAddress,
      verificationCode: data.verificationCode
    });
  }

  /**
   * Password reset
   */
  passwordReset(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/submitpasswordreset`, {
      emailAddress: data.emailAddress,
      newPassword: data.password,
      retypePassword: data.confirmPassword
    });
  }

  /**
   * Verify the password reset
   */
  verifyPasswordReset(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/verifypasswordreset`, {
      emailAddress: data.emailAddress,
      verificationCode: data.verificationCode
    });
  }

  /**
   * Cancel the password reset
   */
  cancelPasswordReset(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/cancelpasswordreset`, {
      emailAddress: data.emailAddress,
      verificationCode: data.verificationCode
    });
  }

  /**
   * Submit email change
   */
  submitEmailChange(sport: string, data: any): Observable<Response> {
    const headers = new HttpHeaders().set(
      'Authorization',
      'Basic ' + btoa(data.emailAddress + ':' + data.password)
    );
    return this.http.post<any>(
      `/api/${sport}/account/submitemailchange`,
      {
        emailAddress: data.emailAddress,
        newEmailAddress: data.newEmailAddress,
        retypeEmailAddress: data.newEmailAddress
      },
      { headers }
    );
  }

  /**
   * Verify email change
   */
  verifyEmailChange(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/verifyemailchange`, {
      emailAddress: data.emailAddress,
      verificationCode: data.verificationCode
    });
  }

  /**
   * Cancel email change
   */
  cancelEmailChange(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/cancelemailchange`, {
      emailAddress: data.emailAddress,
      verificationCode: data.verificationCode
    });
  }

  /**
   * Revoke email change
   */
  revokeEmailChange(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/revokeemailchange`, {
      emailAddress: data.emailAddress,
      verificationCode: data.verificationCode
    });
  }

  /**
   * Void email change
   */
  voidEmailChange(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/voidemailchange`, {
      emailAddress: data.emailAddress,
      verificationCode: data.verificationCode
    });
  }

  /**
   * Password change
   */
  passwordChange(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/submitpasswordchange`, {
      emailAddress: data.emailAddress,
      oldPassword: data.oldPassword,
      newPassword: data.password,
      retypePassword: data.confirmPassword
    });
  }

  /**
   * Verify the password change
   */
  verifyPasswordChange(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/verifypasswordchange`, {
      emailAddress: data.emailAddress,
      verificationCode: data.verificationCode
    });
  }

  /**
   * Cancel the password change
   */
  cancelPasswordChange(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/cancelpasswordchange`, {
      emailAddress: data.emailAddress,
      verificationCode: data.verificationCode
    });
  }

  /**
   * Create a user account
   */
  createUser(sport: string, data: any): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/createuser`, {
      userName: data.userName,
      emailAddress: data.emailAddress,
      password: data.password,
      birthMonth: data.birthdayMonth + 1,
      birthYear: data.birthdayYear,
      armyInterest: data.armyInterest,
      phoneNumber: this.formatPhoneNumber(data.phoneNumber),
      emailOption: data.emailOption ? 'IN' : 'OUT'
    });
  }

  /**
   * Formats the phone number to the correct format XXX-XXX-XXXX
   */
  formatPhoneNumber(phone: string): string {
    const phoneArray = phone.split(' ');
    const phone0 = phoneArray[0].replace(/\D/g, '');
    return `${phone0}-${phoneArray[1]}-${phoneArray[3]}`;
  }

  /**
   * Change Email Address after Request made
   * To change the email address (username) for the account
   * after the request had been made. An email is sent to
   * the new address indicating the email address had been made.
   */
  updateEmail(sport: string, data: EmailData): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/updateemail`, data);
  }

  /**
   * Cancel the Email Request
   * To cancel the email address (username) request for the account
   */
  cancelEmailRequest(sport: string, data: EmailData): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/resetemail`, data);
  }

  /**
   * Unlock Account
   * Unlocks an account with the verification code sent to the user
   * via email when the account is first locked
   */
  unlockAccount(sport: string, data: EmailData): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/unlockaccount`, data);
  }

  /**
   * Resend verification code for Email Request
   * Resends the emails to both the new and old email addresses with
   * links to update or cancel
   */
  resendVCode(
    sport: string,
    data: { emailAddress: string }
  ): Observable<Response> {
    return this.http.post<any>(`/api/${sport}/account/resendemailcode`, data);
  }
}
